import React from 'react';
import { TFunction } from 'i18next';
import {
  Documentation,
  DocumentationValidationStatusEnum,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { DocumentAnswerStatus } from './getDocumentAnswerStatuses';
import styles from './DocumentResponseCell.module.scss';

type Translate = TFunction<'translation', undefined>;

// Building blocks shared by `DocumentResponseCell` (one completion's column)
// and the compare table's split identity/status cells.

export function getDocumentIds(options: FormTemplateQuestionFieldOption[]) {
  const ids = new Set<string>();
  options.forEach((opt) => {
    const id = opt.optionDocumentation?.documentationId;
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

// EDM-locked render: when the company plan strips optionDocumentation (BE
// returns null for every option), show an upgrade affordance to Full Admins
// and a neutral "unavailable" message to everyone else.
export function DocumentLockedMessage({
  t,
  isFullAdmin,
  onUpgradePlan,
}: {
  t: Translate;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
}) {
  return (
    <div className={styles.supportiveMessageWrap}>
      {isFullAdmin ? (
        <SupportiveMessage
          color={SupportiveMessageColor.Yellow}
          text={t('advanced-documentation-features-not-in-plan')}
          actions={
            <Button
              buttonText={t('upgrade-plan')}
              buttonType={ButtonTypes.primaryFilled}
              onClick={onUpgradePlan}
            />
          }
          hasBottomSpace={false}
        />
      ) : (
        <SupportiveMessage
          color={SupportiveMessageColor.Gray}
          text={t('document-unavailable')}
          leftIconSymbolsOutlined
          hasBottomSpace={false}
        />
      )}
    </div>
  );
}

export function DocumentPreviewOrUnavailable({
  t,
  doc,
  docId,
  onDocumentClick,
}: {
  t: Translate;
  doc?: Documentation;
  docId: string;
  onDocumentClick?: (documentationId: string) => void;
}) {
  if (!doc) {
    return (
      <SupportiveMessage
        text={t('document-unavailable')}
        leftIconSymbolsOutlined
        color={SupportiveMessageColor.Gray}
        hasBottomSpace={false}
      />
    );
  }
  return (
    <DocumentationPreview
      attachment={doc.currentVersion?.attachmentAttached}
      customName={doc.name}
      view={DocumentationPreviewEnum.RowCard}
      notValidated={
        doc.validationStatus !== DocumentationValidationStatusEnum.Validated
      }
      onClick={onDocumentClick ? () => onDocumentClick(docId) : undefined}
    />
  );
}

export function DocumentReadChip({
  t,
  status,
}: {
  t: Translate;
  status: Pick<
    DocumentAnswerStatus,
    'isReadAny' | 'isRead' | 'isReadPreviously'
  >;
}) {
  const { isReadAny, isRead, isReadPreviously } = status;
  let label = t('unread');
  if (isRead) label = t('read');
  else if (isReadPreviously) label = t('read-previously');
  return (
    <Chip
      type={isReadAny ? ChipTypes.chipGreen : ChipTypes.chipRed}
      label={label}
      size={ChipSizes.small}
    />
  );
}
